import { Component, OnInit, OnDestroy, ViewChild, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppComponent } from '../app.component';
import { bmxurl } from 'src/globals';
import { ActivatedRoute, Router } from '@angular/router';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { BusyService } from '../_services/busy.service';
import { GlobalService } from '../_services/global.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-counterbookinglist',
  templateUrl: './counterbookinglist.component.html',
  styleUrls: ['./counterbookinglist.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CounterbookinglistComponent implements OnInit, OnDestroy {

  @ViewChild('resourceTable') resourceTable: Table;

  ListBookings: any[] = [];
  VenueID: string = localStorage.getItem("SmartVenueID")!;
  CurrencySymbol: string = localStorage.getItem("Currency") || "GBP";
  counterType: string = "";
  pageTitle: string = "";

  //Scanner input, and the booking it turned up.
  scanBuffer: string = '';
  scanTimer: any = null;

  //TEMPORARY - a box to type a Sales ID into and send it through handleScan, for testing without
  //a scanner. Only shown on a non-production build, so it cannot reach venues if left in.
  //Remove scanTest*, simulateScan() and the "Scanner test" box in the page once scanning has
  //been tried with a real scanner.
  readonly scanTestShown: boolean = !environment.production;
  scanTestValue: string = '';

  simulateScan() {
    const value = (this.scanTestValue ?? '').trim();

    if (value == '') {
      return;
    }

    this.handleScan(value);
    this.scanTestValue = '';
  }
  showScanDialog: boolean = false;
  scannedBooking: any = null;

  private typeTitleMap: { [key: string]: string } = {
    participants: "Participants",
    nonparticipants: "Non-Participants",
    timeremaining: "Time Remaining",
    timeusedup: "Time Used Up",
    total: "Total"
  };

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private appComp: AppComponent,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) {
  }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);

    this.counterType = this.route.snapshot.paramMap.get('type')!;
    this.pageTitle = this.typeTitleMap[this.counterType] || this.counterType;

    this.loadgrid();
  }

  loadgrid(): void {
    this.busyService.busy();
    this.http.get<any>(bmxurl + "todayscounters/" + this.VenueID + "/" + this.counterType).subscribe(response => {
      if (response.succeeded) {
        this.ListBookings = response.data || [];
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not retrieve bookings for this counter type.' });
      }
      this.busyService.idle();
    }, error => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred retrieving bookings.' });
      this.busyService.idle();
    });
  }

  editBooking(bookingId: string): void {
    localStorage.setItem("counterBookingList", this.counterType);
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  goBack(): void {
    this.router.navigateByUrl("/ataglance");
  }

  maskMobile(mobileIn: string): string {
    if (mobileIn != null && mobileIn.length > 4) {
      return "**** **** " + mobileIn.slice(-4);
    }
    return mobileIn || "";
  }

  admitall(id: string): void {
    this.http.get(bmxurl + "BookingCheckin/" + this.VenueID + "/" + id).subscribe(response => {
      let res = response as any;
      if (res.succeeded) {
        this.messageService.add({ severity: 'success', summary: 'Admitted', detail: "All customers have been marked as 'admitted' for this booking." });
        this.updategrid(id, "admit");
      }
    }, () => {
      this.messageService.add({ severity: 'error', summary: 'Not Admitted', detail: 'An error occurred admitting all customers for this booking.' });
    });
  }

  leaveall(id: string): void {
    this.http.get(bmxurl + "BookingCheckout/" + this.VenueID + "/" + id).subscribe(response => {
      let res = response as any;
      if (res.succeeded) {
        this.messageService.add({ severity: 'success', summary: 'Left', detail: "All customers have been marked as 'left' for this booking." });
        this.updategrid(id, "leave");
      }
    }, () => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred checking out customers for this booking.' });
    });
  }

  updategrid(id: string, admitleave: string): void {
    let booking = this.ListBookings.find(b => b.bookingId === id);
    if (booking) {
      booking.checkedin = admitleave === "admit";
    }
  }

  goToManageWaivers(bookingId: string): void {
    const today = new Date().toISOString().split('T')[0];
    this.router.navigate(['/managewaivers', bookingId], { queryParams: { returnDate: today } });
  }

  //#region Scanning
  //Same keyboard-wedge handling as At a Glance: characters arrive far faster than anyone types,
  //so a gap of 100ms ends the scan and Enter submits it.
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;

    if (event.key === 'Enter') {
      if (this.scanBuffer.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        this.handleScan(this.scanBuffer);
      }
      this.scanBuffer = '';
      if (this.scanTimer) { clearTimeout(this.scanTimer); this.scanTimer = null; }
      return;
    }

    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    if (event.key.length !== 1) { return; }

    //Otherwise the scan's Enter also presses whatever button was last touched.
    if (target.tagName === 'BUTTON') {
      target.blur();
    }

    if (this.scanTimer) { clearTimeout(this.scanTimer); }
    this.scanBuffer += event.key;
    this.scanTimer = setTimeout(() => { this.scanBuffer = ''; this.scanTimer = null; }, 100);
  }

  handleScan(bookingIdHR: string) {
    const ref = bookingIdHR.trim().replace('#','');
    if (ref.length < 3) { return; }

    //A scanner gives the sales ID printed on the ticket, so the reference is looked up the way
    //the Sales ID box in the header does.
    const bookingRefPattern = /^[a-zA-Z]{3}-\d{6}-[a-zA-Z0-9]{6}$/;
    const showDisabled = bookingRefPattern.test(ref);

    this.http.get(bmxurl + "Booking/Search/" + this.VenueID + "/" + encodeURIComponent(ref) + "/" + showDisabled).subscribe(response => {
      const result = response as any;
      const found: any[] = (result != null && result.succeeded ? result.data : null) ?? [];

      const match = found.find(booking => (booking.bookingIdHR ?? "").toLowerCase() == ref.toLowerCase())
        ?? (found.length == 1 ? found[0] : null);

      if (match == null) {
        this.messageService.add({ severity: 'warn', summary: 'Not Found', detail: 'No booking found for reference: ' + ref });
        return;
      }

      //A ticket for another day is not turned away here. It opens like any other and says so
      //at the top of the dialog, the same as At a Glance does.
      //
      //This page is about people leaving, so the search row is all that is needed - there is no
      //second call for the allergy and admissions the At a Glance dialog shows.
      this.scannedBooking = match;
      this.scannedBooking.notForToday = !this.isForToday(match.startTime);
      this.scannedBooking.notForTodayText = this.scannedBooking.notForToday
        ? this.describeOtherDay(match.startTime)
        : "";
      this.showScanDialog = true;
    }, () => {
      this.messageService.add({ severity: 'error', summary: 'Scan Error', detail: 'Could not find booking for: ' + ref });
    });
  }

  //A booking is held in UTC and the venue reads it as its own clock, so the day is taken from
  //the UTC parts rather than the browser's.
  private isForToday(startTime: any): boolean {
    if (startTime == null) { return false; }

    const start = new Date(startTime);
    if (isNaN(start.getTime())) { return false; }

    const now = new Date();

    return start.getUTCFullYear() == now.getFullYear()
      && start.getUTCMonth() == now.getMonth()
      && start.getUTCDate() == now.getDate();
  }

  //Which day the ticket is for, in the words whoever scanned it needs: a day either side is
  //worth naming, and a day already gone is worth saying out loud.
  private describeOtherDay(startTime: any): string {
    const start = new Date(startTime);

    if (isNaN(start.getTime())) { return "another day"; }

    const written = this.readableBookingDate(startTime);
    const now = new Date();
    const days = Math.round(
      (Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
        - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);

    if (days == -1) { return "yesterday, " + written; }
    if (days == 1) { return "tomorrow, " + written; }

    return written + (days < 0 ? ", which has passed" : "");
  }

  private readableBookingDate(startTime: any): string {
    const start = new Date(startTime);

    return ('00' + start.getUTCDate()).slice(-2)
      + '/' + ('00' + (start.getUTCMonth() + 1)).slice(-2)
      + '/' + start.getUTCFullYear();
  }

  scannedBookingIsAdmitted(): boolean {
    return this.scannedBooking != null && this.scannedBooking.checkedin === true;
  }

  leaveScannedBooking() {
    if (this.scannedBooking == null) { return; }

    const bookingId = this.scannedBooking.bookingId;

    this.http.get(bmxurl + "BookingCheckout/" + this.VenueID + "/" + bookingId).subscribe(response => {
      const result = response as any;

      if (result.succeeded) {
        this.messageService.add({ severity: 'success', summary: 'Left', detail: "All customers have been marked as 'left' for this booking." });
        this.closeScanDialog();
        this.loadgrid();
      }
      else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred checking out customers for this booking.' });
      }
    }, () => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred checking out customers for this booking.' });
    });
  }

  closeScanDialog() {
    this.showScanDialog = false;
    this.scannedBooking = null;
  }

  ngOnDestroy() {
    if (this.scanTimer) { clearTimeout(this.scanTimer); }
  }
  //#endregion
}
